import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { RadioGroup } from '../ui/RadioGroup';
import { CheckboxGroup } from '../ui/CheckboxGroup';
import { Textarea } from '../ui/Textarea';
import { MascotPanel } from '../ui/MascotPanel';
import { Tooltip } from '../ui/Tooltip';
import { InfoIcon } from 'lucide-react';
function LabelWithInfo({
  label,
  tooltip
}: {
  label: string;
  tooltip: string;
}) {
  return <div className="flex items-center mb-3" data-id="element-89">
      <label className="block text-sm font-semibold text-gray-900" data-id="element-90">
        {label}
      </label>
      <Tooltip content={tooltip} data-id="element-91">
        <button type="button" className="ml-2 text-gray-400 hover:text-[#007AFF] focus:outline-none" data-id="element-92">
          <InfoIcon className="w-4 h-4" data-id="element-93" />
        </button>
      </Tooltip>
    </div>;
}
export function Step13SEO() {
  const {
    data,
    updateData
  } = useIntake();
  const isSeoImportant = data.seoServices === 'high' || data.seoServices === 'medium';
  const googleBusinessTasks = [{
    value: 'photos',
    label: "Foto's uploaden"
  }, {
    value: 'blogs',
    label: 'Blogs / Updates plaatsen'
  }, {
    value: 'info',
    label: 'Bedrijfsinformatie juist zetten'
  }];
  const socialAdsOptions = [{
    value: 'instagram',
    label: 'Instagram'
  }, {
    value: 'facebook',
    label: 'Facebook'
  }, {
    value: 'tiktok',
    label: 'TikTok'
  }, {
    value: 'linkedin',
    label: 'LinkedIn'
  }, {
    value: 'pinterest',
    label: 'Pinterest'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-3xl mx-auto space-y-8" data-id="element-94">
      <MascotPanel message="Een website is pas succesvol als hij gevonden wordt. Hoe belangrijk is Google voor uw bedrijf?" detailedInfo="SEO (Zoekmachine Optimalisatie) zorgt ervoor dat uw website hoger in Google komt. Als u dit belangrijk vindt, kunnen we een uitgebreid onderzoek doen naar zoektermen, een SEO silo opzetten (een slimme websitestructuur), of u helpen met blogs en Google Bedrijfsprofiel. Als u kiest voor 'Niet zo belangrijk', installeren we standaard Yoast SEO voor de basis." data-id="element-95" />

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-8" data-id="element-96">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-97">Vindbaarheid (SEO)</h2>

        <div data-id="element-98">
          <LabelWithInfo label="Hoe belangrijk is vindbaarheid in Google voor u?" tooltip="SEO staat voor Search Engine Optimization (zoekmachine optimalisatie). Het zorgt ervoor dat uw website hoger verschijnt in Google wanneer potentiële klanten zoeken naar uw diensten of producten." data-id="element-99" />
          <RadioGroup columns={1} value={data.seoServices} onChange={val => updateData({
          seoServices: val
        })} options={[{
          value: 'high',
          label: 'Zeer belangrijk',
          description: 'We willen actief groeien via Google en concurrenten verslaan'
        }, {
          value: 'medium',
          label: 'Belangrijk',
          description: 'De basis moet goed staan voor relevante zoektermen'
        }, {
          value: 'low',
          label: 'Niet zo belangrijk',
          description: 'Klanten komen voornamelijk via ons eigen netwerk (Standaard Yoast SEO)'
        }]} data-id="element-100" />
        </div>

        <AnimatePresence data-id="element-101">
          {isSeoImportant && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="space-y-8 pt-6 border-t border-gray-100 overflow-hidden" data-id="element-102">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-103">
                Uitgebreide SEO Wensen
              </h3>

              <Textarea label="Op welke zoektermen wilt u idealiter gevonden worden?" infoTooltip="Denk aan de woorden die uw ideale klant intypt in Google als ze op zoek zijn naar uw diensten. Bijv. 'Loodgieter Amsterdam' of 'Schoenen kopen online'." placeholder="Als u ze al weet, vul ze in. Anders doen wij onderzoek..." value={data.seoSearchTerms} onChange={e => updateData({
            seoSearchTerms: e.target.value
          })} data-id="element-104" />

              <div data-id="element-105">
                <LabelWithInfo label="Wat voor zoekwoordenonderzoek wilt u?" tooltip="Bij zoekwoordenonderzoek analyseren we welke woorden en zinnen uw doelgroep gebruikt in Google. 'Long-tail' zoektermen zijn langere, specifiekere zoekopdrachten (bijv. 'beste loodgieter spoedservice Amsterdam') die minder concurrentie hebben en vaak beter converteren." data-id="element-106" />
                <RadioGroup columns={1} value={data.seoKeywordResearch} onChange={val => updateData({
              seoKeywordResearch: val
            })} options={[{
              value: 'extensive',
              label: 'Uitgebreid onderzoek (inclusief long-tail zoektermen)'
            }, {
              value: 'small',
              label: 'Klein basis onderzoek'
            }, {
              value: 'none',
              label: 'Niet nodig, ik weet de termen al'
            }]} data-id="element-107" />
              </div>

              <div data-id="element-108">
                <LabelWithInfo label="Wilt u dat wij SEO-geoptimaliseerde blogposts schrijven?" tooltip="Regelmatige blogposts helpen uw website hoger in Google te komen. Google beloont websites die regelmatig nieuwe, relevante content publiceren. Elke blogpost is een extra kans om gevonden te worden op nieuwe zoektermen." data-id="element-109" />
                <RadioGroup columns={2} value={data.seoBlogposts} onChange={val => updateData({
              seoBlogposts: val
            })} options={[{
              value: 'yes',
              label: 'Ja, graag'
            }, {
              value: 'no',
              label: 'Nee, doe ik zelf'
            }]} data-id="element-110" />

                <AnimatePresence data-id="element-111">
                  {data.seoBlogposts === 'yes' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="mt-4 overflow-hidden" data-id="element-112">
                      <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-113">
                        Hoe vaak wilt u dat wij een blogpost plaatsen?
                      </label>
                      <RadioGroup columns={2} value={data.seoBlogFrequency} onChange={val => updateData({
                  seoBlogFrequency: val
                })} options={[{
                  value: 'daily',
                  label: 'Elke dag'
                }, {
                  value: 'weekly',
                  label: '1x per week'
                }, {
                  value: 'biweekly',
                  label: '1x per twee weken'
                }, {
                  value: 'monthly',
                  label: '1x per maand'
                }]} data-id="element-114" />
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div data-id="element-115">
                <LabelWithInfo label="Wilt u een SEO Silo structuur?" tooltip="Een SEO silo is een slimme manier om uw website te organiseren. Gerelateerde pagina's worden gegroepeerd in 'silo's' (thema's), waardoor Google beter begrijpt waar uw website over gaat. Dit kan uw positie in zoekresultaten flink verbeteren." data-id="element-116" />
                <RadioGroup columns={2} value={data.seoSilo} onChange={val => updateData({
              seoSilo: val
            })} options={[{
              value: 'yes',
              label: 'Ja, stel een silo in'
            }, {
              value: 'no',
              label: 'Nee, niet nodig'
            }, {
              value: 'dont_know',
              label: 'Weet ik niet, adviseer mij'
            }]} data-id="element-117" />
              </div>

              <div data-id="element-118">
                <LabelWithInfo label="Wilt u dat de zoekwoorden regelmatig geanalyseerd en aangepast worden?" tooltip="Google's algoritme verandert continu en uw concurrenten zitten ook niet stil. Door regelmatig te analyseren welke zoektermen goed presteren en welke niet, kunnen we uw strategie bijsturen voor optimaal resultaat." data-id="element-119" />
                <RadioGroup columns={2} value={data.seoActiveUpdates} onChange={val => updateData({
              seoActiveUpdates: val
            })} options={[{
              value: 'monthly',
              label: 'Ja, maandelijks'
            }, {
              value: 'yearly',
              label: 'Ja, jaarlijks'
            }, {
              value: 'no',
              label: 'Nee, eenmalige setup is genoeg'
            }]} data-id="element-120" />
              </div>

              <div data-id="element-121">
                <LabelWithInfo label="Wilt u inzicht in waar bezoekers klikken en kijken (Heatmaps)?" tooltip="Heatmaps zijn visuele kaarten die laten zien waar bezoekers op uw website klikken, scrollen en kijken. Hiermee ziet u precies welke onderdelen van uw pagina aandacht krijgen en welke genegeerd worden — cruciaal om uw website te verbeteren." data-id="element-122" />
                <RadioGroup columns={2} value={data.seoHeatmapsUrgency} onChange={val => updateData({
              seoHeatmapsUrgency: val
            })} options={[{
              value: 'yes',
              label: 'Ja, ik wil dit inzicht'
            }, {
              value: 'no',
              label: 'Nee, niet nodig'
            }, {
              value: 'advise',
              label: 'Weet ik niet, adviseer mij'
            }]} data-id="element-123" />
              </div>

              <div data-id="element-124">
                <LabelWithInfo label="Wilt u verschillende versies van uw website testen (A/B Testing)?" tooltip="Bij A/B testing tonen we twee verschillende versies van een pagina aan verschillende bezoekers. Zo ontdekken we welke versie beter werkt — bijv. welke knopkleur of tekst meer aanvragen oplevert. Dit is de meest wetenschappelijke manier om uw conversie te verhogen." data-id="element-125" />
                <RadioGroup columns={2} value={data.seoABTestingUrgency} onChange={val => updateData({
              seoABTestingUrgency: val
            })} options={[{
              value: 'yes',
              label: 'Ja, ik wil testen'
            }, {
              value: 'no',
              label: 'Nee, niet nodig'
            }, {
              value: 'advise',
              label: 'Weet ik niet, adviseer mij'
            }]} data-id="element-126" />
              </div>

              <div data-id="element-127">
                <LabelWithInfo label="Wilt u extra training over uw website, SEO en bedrijfsprofielen?" tooltip="Bij oplevering krijgt u altijd een basistraining. Met extra training leert u zelf hoe u blogs plaatst, reageert op reviews, uw Google profiel bijhoudt en de SEO-prestaties monitort. Zo bent u zelfstandig en bespaart u op de lange termijn." data-id="element-128" />
                <RadioGroup columns={2} value={data.seoTraining} onChange={val => updateData({
              seoTraining: val
            })} options={[{
              value: 'yes',
              label: 'Ja, ik wil extra training'
            }, {
              value: 'no',
              label: 'Nee, basis is voldoende'
            }]} data-id="element-129" />
              </div>

              <div className="pt-6 border-t border-gray-100" data-id="element-130">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-131">
                  Bedrijfsprofielen & Advertenties
                </h3>

                <div className="mb-6" data-id="element-132">
                  <LabelWithInfo label="Wilt u hulp met uw Google Bedrijfsprofiel?" tooltip="Uw Google Bedrijfsprofiel (voorheen Google Mijn Bedrijf) is het informatieblok dat rechts verschijnt als iemand uw bedrijfsnaam googelt. Het toont uw adres, openingstijden, reviews en foto's. Een goed onderhouden profiel vergroot uw lokale vindbaarheid enorm." data-id="element-133" />
                  <RadioGroup columns={2} value={data.seoGoogleBusiness} onChange={val => updateData({
                seoGoogleBusiness: val
              })} options={[{
                value: 'yes',
                label: 'Ja'
              }, {
                value: 'no',
                label: 'Nee'
              }]} data-id="element-134" />

                  <AnimatePresence data-id="element-135">
                    {data.seoGoogleBusiness === 'yes' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="mt-4 overflow-hidden" data-id="element-136">
                        <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-137">
                          Wat wilt u dat wij doen voor uw Google profiel?
                        </label>
                        <CheckboxGroup columns={1} options={googleBusinessTasks} values={data.seoGoogleBusinessTasks} onChange={vals => updateData({
                    seoGoogleBusinessTasks: vals
                  })} data-id="element-138" />
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div className="mb-6" data-id="element-139">
                  <LabelWithInfo label="Wilt u ook hulp met uw Apple Bedrijfsprofiel (Apple Maps)?" tooltip="Apple Maps wordt standaard gebruikt op iPhones en iPads. Een Apple Bedrijfsprofiel zorgt dat uw bedrijf ook daar correct en compleet wordt weergegeven — belangrijk aangezien meer dan de helft van de Nederlanders een iPhone gebruikt." data-id="element-140" />
                  <RadioGroup columns={2} value={data.seoAppleBusiness} onChange={val => updateData({
                seoAppleBusiness: val
              })} options={[{
                value: 'yes',
                label: 'Ja'
              }, {
                value: 'no',
                label: 'Nee'
              }]} data-id="element-141" />

                  <AnimatePresence data-id="element-142">
                    {data.seoAppleBusiness === 'yes' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="mt-4 overflow-hidden" data-id="element-143">
                        <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-144">
                          Wat wilt u dat wij doen voor uw Apple profiel?
                        </label>
                        <CheckboxGroup columns={1} options={googleBusinessTasks} values={data.seoAppleBusinessTasks} onChange={vals => updateData({
                    seoAppleBusinessTasks: vals
                  })} data-id="element-145" />
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div className="mb-6" data-id="element-146">
                  <LabelWithInfo label="Wilt u adverteren via Google Ads (SEA)?" tooltip="Google Ads (SEA = Search Engine Advertising) zijn de betaalde advertenties bovenaan de Google zoekresultaten. In tegenstelling tot SEO (organisch) betaalt u per klik. Het voordeel: u bent direct zichtbaar voor uw doelgroep. Het nadeel: zodra u stopt met betalen, verdwijnt u weer." data-id="element-147" />
                  <RadioGroup columns={2} value={data.seoGoogleAds} onChange={val => updateData({
                seoGoogleAds: val
              })} options={[{
                value: 'yes',
                label: 'Ja'
              }, {
                value: 'no',
                label: 'Nee'
              }, {
                value: 'advise',
                label: 'Adviseer mij'
              }]} data-id="element-148" />
                </div>

                <div data-id="element-149">
                  <LabelWithInfo label="Wilt u advertenties draaien op Social Media?" tooltip="Social media advertenties verschijnen in de feeds van uw doelgroep op platforms als Instagram, Facebook en LinkedIn. U kunt zeer gericht targeten op leeftijd, locatie, interesses en gedrag. Ideaal om naamsbekendheid te vergroten of specifieke acties te promoten." data-id="element-150" />
                  <CheckboxGroup columns={2} options={socialAdsOptions} values={data.seoSocialAds} onChange={vals => updateData({
                seoSocialAds: vals
              })} data-id="element-151" />
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-xl text-sm text-gray-600" data-id="element-152">
                  <strong data-id="element-153">Opmerking:</strong> Zaken zoals technische SEO en LLM
                  SEO (AI search) worden automatisch door ons en Yoast SEO
                  meegenomen.
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}